import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Send, Users, FolderOpen, Clock, CheckCircle } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SendMatchPage() {
  const { getAuthHeaders, user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [users, setUsers] = useState([]);
  const [sentMatches, setSentMatches] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dealsRes, usersRes, sentRes] = await Promise.all([
        axios.get(`${API}/deals`, { headers: getAuthHeaders() }),
        axios.get(`${API}/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/sent-matches`, { headers: getAuthHeaders() })
      ]);
      setDeals(dealsRes.data);
      setUsers(usersRes.data.filter(u => u.role !== 'admin' && u.role !== 'internal'));
      setSentMatches(sentRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMatch = async () => {
    if (!selectedDeal || !selectedUser) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/send-match`, {
        deal_id: selectedDeal,
        user_id: selectedUser,
        message
      }, { headers: getAuthHeaders() });
      toast.success('Deal recommendation sent successfully');
      setSelectedDeal('');
      setSelectedUser('');
      setMessage('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send match');
    } finally {
      setSending(false);
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'internal') {
    return <div className="p-6 text-center text-muted-foreground">Admin access required</div>;
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="send-match-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Send Deal Recommendations</h1>
        <p className="text-muted-foreground">Manually push deal recommendations to participants</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Matches Sent</p>
            <p className="text-2xl font-bold font-mono">{sentMatches.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Active Deals</p>
            <p className="text-2xl font-bold font-mono">{deals.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Participants</p>
            <p className="text-2xl font-bold font-mono">{users.length}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Send size={16} />Send New Recommendation
          </CardTitle>
          <CardDescription>Select a deal and a participant to send a recommendation</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Select Deal</Label>
              <Select value={selectedDeal} onValueChange={setSelectedDeal}>
                <SelectTrigger data-testid="select-deal">
                  <SelectValue placeholder="Choose a deal..." />
                </SelectTrigger>
                <SelectContent>
                  {deals.map(deal => (
                    <SelectItem key={deal.id} value={deal.id}>
                      {deal.name} ({formatCurrency(deal.capital_required)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Select Participant</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger data-testid="select-user">
                  <SelectValue placeholder="Choose a participant..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name} ({u.role} - {u.organization})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Message (Optional)</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a note about why this deal is a good match..."
              rows={3}
              data-testid="match-message"
            />
          </div>
          <Button onClick={sendMatch} disabled={!selectedDeal || !selectedUser || sending} data-testid="send-match-btn">
            <Send size={16} className="mr-2" />{sending ? 'Sending...' : 'Send Recommendation'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock size={16} />Sent History
          </CardTitle>
          <CardDescription>Previously sent deal recommendations</CardDescription>
        </CardHeader>
        <CardContent>
          {sentMatches.length === 0 ? (
            <p className="text-center text-muted-foreground py-8 text-sm">No recommendations sent yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Deal</TableHead>
                  <TableHead>Sent To</TableHead>
                  <TableHead>Sent By</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sentMatches.map(match => (
                  <TableRow key={match.id} data-testid={`sent-match-${match.id}`}>
                    <TableCell className="font-medium text-sm">{match.deal_name}</TableCell>
                    <TableCell className="text-sm">{match.user_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{match.sent_by_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{match.message || '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {match.created_at ? new Date(match.created_at).toLocaleDateString() : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
